import React, {useEffect, useRef, useState} from "react";
import {styled} from "@mui/material/styles";
import {CircularProgress} from "@mui/material";
import InputAdornment from "@mui/material/InputAdornment";

import Input from "@/components/form/MUIInput.jsx";
import {useFetchData} from "@/hooks/useFetchData.jsx";
import {LOCATION_SEARCH_ENDPOINT} from "@/constants/endpoints.jsx";
import {COLOR_BLACK, COLOR_BLUE, COLOR_BLUE_SECONDARY, COLOR_WHITE} from "@/themes/colors.jsx";

let locationSearchTimer = null;

export default function LocationSearchInput({value, initLocation, searchType = 'cities', onFocus, onChange, onLoading, onEndLoading, ...props}) {
    const {
        data: searchResults,
        isLoading,
        fetchData,
        clearFetchedData
    } = useFetchData({endpoint: LOCATION_SEARCH_ENDPOINT});

    const [keyword, setKeyword] = useState(value ?? '');
    const [location, setLocation] = useState(initLocation);

    const [searchResultsIsOpened, setSearchResultsIsOpened] = useState(false);
    const [searchResultsFocusedIndex, setLocationSearchResultsFocusedIndex] = useState(-1);

    let searchInputRef = useRef(null);
    let searchResultsContainerRef = useRef(null);
    let searchResultFocusedItemRef = useRef(null);

    useEffect(() => {
        function handleClickOutsideSearch(event) {
            if(searchResultsContainerRef.current && searchResultsContainerRef.current.contains(event.target)) {
                return true;
            }

            if (searchInputRef.current && !searchInputRef.current.contains(event.target)) {
                setSearchResultsIsOpened(false);
            } else {
                setSearchResultsIsOpened(true);
            }
        }

        document.addEventListener("mousedown", handleClickOutsideSearch);
        return () => {
            document.removeEventListener("mousedown", handleClickOutsideSearch);
        };
    }, [searchInputRef]);

    useEffect(() => {
        setLocation(initLocation);
    }, [initLocation]);

    useEffect(() => {
        if(isLoading && typeof onLoading == 'function'){
            onLoading();
        }

        if(!isLoading && typeof onEndLoading == 'function'){
            onEndLoading();
        }
    }, [isLoading]);

    useEffect(() => {
        setKeyword(value);
    }, [value]);

    useEffect(() => {
        setSearchResultsIsOpened(true);
    }, [searchResults]);

    useEffect(() => {
        if (searchResultsIsOpened == false && keyword?.length > 0 && !location?.id) {
            const firstItem = searchResults.length > 0 ? searchResults[0] : null;
            if (firstItem) {
                handleSearchResultItemOnClick(firstItem);
            }
        }
    }, [searchResultsIsOpened]);

    useEffect(() => {
        if (searchResultFocusedItemRef.current && searchResultsContainerRef.current) {
            searchResultsContainerRef.current.scrollTop = searchResultFocusedItemRef.current.offsetTop;
        }
    }, [searchResultsFocusedIndex]);

    const handleLocationChange = (e) => {
        e.persist();
        setKeyword(e.target.value);
        setLocation({});
        if (typeof onChange === "function") {
            onChange({}, e.target.value);
        }
    };

    const handleLocationInputKeyDown = (e) => {
        if (e.keyCode === 13 || e.keyCode === 40 || e.keyCode === 38 || e.keyCode === 39) {
            return true;
        }
    };

    const handleLocationInputKeyUp = (e) => {
        if (e.keyCode === 13 || e.keyCode === 40 || e.keyCode === 38 || e.keyCode === 39) {
            if (e.keyCode === 13/*enter*/ && searchResultsFocusedIndex != null) {
                if (searchResultFocusedItemRef.current) {
                    searchResultFocusedItemRef.current.click();
                } else if (keyword?.length > 0 && !location?.id) {
                    const firstItem = searchResults.length > 0 ? searchResults[0] : null;
                    if (firstItem) {
                        handleSearchResultItemOnClick(firstItem);
                    }
                }
            } else if (e.keyCode === 40/*down*/) {
                let newFocus = searchResultsFocusedIndex + 1;
                if (newFocus > searchResults.length - 1) {
                    newFocus = 0;
                }
                setLocationSearchResultsFocusedIndex(newFocus);
            } else if (e.keyCode === 38/*up*/) {
                let newFocus = searchResultsFocusedIndex - 1;
                if (newFocus < 0) {
                    newFocus = searchResults.length - 1;
                }
                setLocationSearchResultsFocusedIndex(newFocus);
            }

            return false;
        }

        if (keyword?.length >= 2) {
            clearTimeout(locationSearchTimer);
            locationSearchTimer = setTimeout(() => {
                setLocationSearchResultsFocusedIndex(-1);
                fetchData({s: keyword, type: searchType});
            }, 400);
        } else {
            searchResultFocusedItemRef.current = null;
            clearFetchedData();
        }
    };

    const handleSearchResultItemOnClick = (selectedItem) => {
        setKeyword(selectedItem?.name);
        setLocation(selectedItem);
        if (typeof onChange === "function") {
            onChange(selectedItem, selectedItem?.name ?? '');
        }
        setSearchResultsIsOpened(false);
    }

    return (
        <Container>
            <SearchInputContainer ref={searchInputRef}>
                <Input
                    {...props}
                    value={keyword}
                    onChange={handleLocationChange}
                    onKeyDown={handleLocationInputKeyDown}
                    onKeyUp={handleLocationInputKeyUp}
                    onFocus={onFocus}
                    endAdornment={
                        <InputAdornment position="end">
                            {isLoading ? <CircularProgress size={18}/> : null}
                        </InputAdornment>
                    }
                />
            </SearchInputContainer>
            {keyword?.length > 0 && searchResults.length > 0 && searchResultsIsOpened &&
                <SearchResultsContainer ref={searchResultsContainerRef} className={'search-items-container'}>
                    {searchResults.map((result, key) => {
                        return (
                            <SearchResultItem
                                ref={ref => {
                                    if (searchResultsFocusedIndex == key) {
                                        searchResultFocusedItemRef.current = ref;
                                    }
                                }}
                                focused={searchResultsFocusedIndex == key}
                                key={result.id}
                                onClick={() => handleSearchResultItemOnClick(result)}
                                dangerouslySetInnerHTML={{__html: result.name}}/>
                        );
                    })}
                </SearchResultsContainer>
            }
        </Container>
    );
}

const Container = styled('div')({
    position: "relative",
    width: '100%',
});

const SearchInputContainer = styled('div')({
    width: '100%',
});

const SearchResultsContainer = styled('div')({
    flexDirection: 'column',
    background: '#FFFFFF',
    // boxShadow: '0 0 10px 0 rgba(93,93,93,0.75)',
    border: `1px solid ${COLOR_BLACK}`,
    position: 'absolute',
    top: '55px',
    left: 0,
    right: 0,
    maxHeight: '172px',
    overflowY: 'auto',
    zIndex: 10,
});

const SearchResultItem = styled('div')(({focused}) => ({
    width: '100%',
    cursor: 'pointer',
    padding: '8px 10px',
    fontSize: '17px',
    fontWeight: '600',
    color: focused ? COLOR_WHITE : COLOR_BLACK,
    background: focused ? COLOR_BLUE : '',

    '&:hover': {
        background: COLOR_BLUE_SECONDARY,
        color: COLOR_WHITE,
    }
}));
